import requests from "./httpServices";

/**
 * Lead Services
 * API calls for lead capture submissions management
 */

/**
 * Get all leads for the authenticated user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default 1)
 * @param {number} params.limit - Items per page (default 50)
 * @param {string} params.leadCaptureId - Filter by lead capture element ID
 * @param {string} params.meetingId - Filter by meeting ID
 * @returns {Promise<Object>} - Paginated leads data
 */
export const getAllLeads = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.leadCaptureId) queryParams.append("leadCaptureId", params.leadCaptureId);
  if (params.meetingId) queryParams.append("meetingId", params.meetingId);

  const query = queryParams.toString();
  return requests.get(`/leads${query ? `?${query}` : ""}`);
};

/**
 * Get lead statistics for the authenticated user
 * @returns {Promise<Object>} - Lead statistics
 */
export const getLeadStats = async () => {
  return requests.get("/leads/stats");
};

/**
 * Get a single lead by ID
 * @param {string} id - Lead submission ID
 * @returns {Promise<Object>} - Lead data
 */
export const getLeadById = async (id) => {
  return requests.get(`/leads/${id}`);
};

/**
 * Get leads by lead capture element ID
 * @param {string} leadCaptureId - Lead capture element ID
 * @returns {Promise<Object>} - Leads data
 */
export const getLeadsByElement = async (leadCaptureId) => {
  return requests.get(`/leads/by-element/${leadCaptureId}`);
};

/**
 * Get leads by meeting ID
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object>} - Leads data
 */
export const getLeadsByMeeting = async (meetingId) => {
  return requests.get(`/leads/by-meeting/${meetingId}`);
};

/**
 * Delete a lead submission
 * @param {string} id - Lead submission ID
 * @returns {Promise<Object>} - Success response
 */
export const deleteLead = async (id) => {
  return requests.delete(`/leads/${id}`);
};

/**
 * Export leads as CSV file
 * @param {Object} params - Query parameters
 * @param {string} params.leadCaptureId - Filter by lead capture element ID
 * @param {string} params.meetingId - Filter by meeting ID
 * @returns {Promise<Blob>} - CSV file blob
 */
export const exportLeadsCSV = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.leadCaptureId) queryParams.append("leadCaptureId", params.leadCaptureId);
  if (params.meetingId) queryParams.append("meetingId", params.meetingId);

  const query = queryParams.toString();
  return requests.blob(`/leads/export/csv${query ? `?${query}` : ""}`);
};

/**
 * Submit a lead capture form (called from meeting participants)
 * @param {Object} data - Lead submission data
 * @returns {Promise<Object>} - Success response
 */
export const submitLead = async (data) => {
  return requests.post("/leads/submit", data);
};

export default {
  getAllLeads,
  getLeadStats,
  getLeadById,
  getLeadsByElement,
  getLeadsByMeeting,
  deleteLead,
  exportLeadsCSV,
  submitLead,
};
